<template>
  <div class="communities-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderPage
      :page="currentPageInfo"
      large
    />
    <div class="order">
      <section
        v-if="genderInfos?.description"
        class="section order-last"
      >
        <div
          class="description"
          v-html="genderInfos.description"
        />
      </section>
      <div class="order-first">
        <SearchResults
          :is-loading="isLoading"
          placeholder-text="Rechercher une communauté"
          :min-length="searchMinLength"
          @search-update="searchUpdateHandler"
        >
          <template #results>
            <CommunityContainer
              :items="genderCommunities"
              :no-result-text="noContent"
            />
          </template>
        </SearchResults>
        <ThePagination
          :total-pages="genderCommunitiesPages"
          :current-page="genderCurrentPage"
          :go-to="changePageTo"
        />
      </div>
    </div>
    <section
      v-if="otherUniverses.length"
      class="game-universes section"
    >
      <div class="title-line line">
        <h2 class="title-strat">Les autres genres de communautés</h2>
      </div>
      <UniversesSlider :universes="otherUniverses" />
    </section>
    <div v-show="false">
      <div
        v-for="commu in allGenderCommunities"
        :key="commu.slug"
        class="commu-item"
      >
        <a :href="`/communautes/${commu.slug}`">{{ commu.slug }}</a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: (to) => {
    const loadGenderCommunity = useLoadGenderCommunity()
    return loadGenderCommunity(to)
  },
})

const communitiesStore = useCommunitiesStore()
const {
  genderCommunities,
  allGenderCommunities,
  genderCommunitiesPages,
  genderCurrentPage,
  genderInfos,
} = storeToRefs(communitiesStore)

const universesStore = useUniversesStore()
const { otherUniverses, currentUniverse } = storeToRefs(universesStore)

const link = useLinkPrevNext({
  currentPage: genderCurrentPage,
  pagesCount: genderCommunitiesPages,
})

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: `${currentUniverse.value?.name}`,
      route: ``,
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: `Tests, avis, astuces et solutions Jeux Videos « ${currentUniverse.value?.name} » PS5, PS4`,
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: `Tests, avis, astuces et solutions Jeux Videos « ${currentUniverse.value?.name} » PS5, PS4`,
  description: `Découvrez toutes les communautés « ${currentUniverse.value?.name} » sur PlayStation. Rejoignez les communautés : Jouez, notez et recommandez vos jeux vidéo favoris !`,
  ogTitle: `Toutes les communautés « ${currentUniverse.value?.name} » We Are PlayStation`,
  ogDescription: `Découvrez toutes les communautés « ${currentUniverse.value?.name} » sur PlayStation.`,
  ogImage:
    genderInfos.value?.image?.url ??
    'https://www.weareplaystation.fr/img/toutes-les-communautes.jpg',
})

const communitiesPerPage = ref(60)

const currentPageInfo = computed(() => {
  return {
    title: `Communautés ${currentUniverse.value?.name ?? ''}`,
    image: genderInfos.value?.image?.url ?? '/img/toutes-les-communautes.jpg',
    titleSmall: 'Communautés',
    titleBig: currentUniverse.value?.name ?? '',
    description: `Découvrez toutes les communautés « ${
      currentUniverse.value?.name ?? ''
    } » sur PlayStation.`,
  }
})

const route = useRoute()

const searchData = async () => {
  isLoading.value = true

  await communitiesStore.fetchGenderCommunities({
    page: 1,
    gender: route.params.genderName as string,
    limit: communitiesPerPage.value,
    search: search.value,
  })

  if (!genderCommunities.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent, searchMinLength } =
  useSearching({
    searchData,
  })

const changePageTo = async (index = 1) => {
  if (index === 0) return
  return communitiesStore.fetchGenderCommunities({
    page: index,
    gender: route.params.genderName as string,
    limit: communitiesPerPage.value,
    search: search.value,
  })
}
</script>

<style lang="scss" scoped>
.communities-page {
  min-height: 100vh;
  padding-bottom: 10rem;
}

.order {
  display: flex;
  flex-direction: column;

  &-first {
    order: 1;
  }

  &-last {
    order: 2;
    width: 100%;
  }

  .description {
    & :deep(h2) {
      margin-bottom: 1.5rem;
    }

    & :deep(h3) {
      margin-bottom: 1rem;
      font-family: SST;
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 2rem;
      letter-spacing: 0.225rem;
      text-transform: uppercase;
    }

    & :deep(p) {
      margin-bottom: 1rem;
    }

    & :deep(ul) {
      margin-bottom: 1rem;
      padding-left: 1em;
      list-style: disc;
    }
  }
}

@media screen and (max-width: 700px) {
  .communities-page {
    .game-universes {
      padding-right: 0;
      padding-left: 0;
    }
  }
}
</style>
